import { Table } from "@mantine/core";
import { useEffect, useMemo, useState } from "react";
import { Button, Input } from "reactstrap";
import AppSpinner from "../../../../elements/AppSpinner";
import useNamedEntityRecognition from "../../../../api/api-hooks/brie/useNamedEntityRecognition";
import { convertArrayToUniquePropertyArray } from "../../../../utils/GeneralUtils";
import { Tag } from "rsuite";
import AppTagPicker from "../../../../components/AppComponents/AppTagPicker";
import AppEmptyState from "../../../../elements/AppEmptyState";

interface NamedEntityInterface {
  entity: string;
  label: string;
}

interface OptionLabelInterface {
  label: string;
  value: string;
  color: string;
}

const optionLabels = [
  { label: "Location", value: "Location", color: "#E99E9E" },
  { label: "Organization", value: "Organization", color: "#E9CA9E" },
  { label: "Keyword", value: "Keyword", color: "#E9E49E" },
  { label: "Quantity", value: "Quantity", color: "#C5E99E" },
  { label: "Money", value: "Money", color: "#9EE9D3" },
  { label: "Date", value: "Date", color: "#9EC8E9" },
  { label: "Person", value: "Person", color: "#A19EE9" },
];

const NamedEntityRecognition = () => {
  const [text, setText] = useState("");
  const [labels, setLabels] = useState<string[]>([]);
  const [showResult, setShowResult] = useState<boolean>(false);

  let {
    namedEntityRecognition,
    namedEntityRecognitionLoading,
    namedEntityRecognitionValue,
    namedEntityRecognitionError,
  } = useNamedEntityRecognition();

  const handleClick = () => {
    if (!text) return;

    namedEntityRecognition({ text });
    setShowResult(true);
  };

  const handleRestart = () => {
    setText("");
    setShowResult(false);
  };

  // Set initial labels to all present in the result at first.
  useEffect(() => {
    if (!Array.isArray(namedEntityRecognitionValue)) return;

    setLabels(
      convertArrayToUniquePropertyArray(namedEntityRecognitionValue, "label")
    );
  }, [namedEntityRecognitionValue]);

  const filteredData = useMemo(() => {
    return namedEntityRecognitionValue
      ?.sort(
        (
          a: { entity: string; label: string },
          b: { entity: string; label: string }
        ) => a.label.localeCompare(b.label)
      )
      .filter((result: NamedEntityInterface) => labels.includes(result.label));
  }, [namedEntityRecognitionValue, labels]);

  // Calculate what text should be highlighted, based on the named entity results.
  // Uses predefined labels (optionLabels)
  const highlightedString = namedEntityRecognitionValue?.reduce(
    (acc: any, chunk: NamedEntityInterface) => {
      const chunkEntity = chunk.entity;
      const chunkLabel = chunk.label;

      // Ensure label is active (Selected in the TagPicker)
      const foundLabel = labels.find((label) => label === chunkLabel);

      // Don't highlight because the label isn't active.
      if (!foundLabel) return acc;

      const foundLabelObject = optionLabels.find(
        (option) => option.value === chunkLabel
      );

      if (foundLabelObject) {
        return acc.replace(
          chunkEntity,
          `<span style="background-color: ${foundLabelObject.color}; border-radius:3px; white-space: nowrap;">
            ${chunkEntity} <span style="font-weight: bold; font-size: 9px; text-transform: uppercase; color: black;">
              ${foundLabelObject.label}
            </span>
          </span>`
        );
      } else {
        return acc;
      }
    },
    text
  );

  return (
    <>
      <div
        className="d-flex flex-column p-4 w-100 rounded"
        style={{
          boxShadow: "0px 1px 8px 2px rgba(0, 0, 0, 0.15)",
        }}
      >
        <div className="d-flex w-100 align-items-center mb-3">
          <i
            className="mdi mdi mdi-text-recognition mr-3"
            style={{ fontSize: "32px" }}
          ></i>
          <h2 className="m-0">Named Entity Recognition</h2>
        </div>
        <p className="text-sm text-dark mb-1">
          Enter your text to extract named entities:
        </p>
        <Input
          className="w-100"
          type="textarea"
          placeholder="Enter or paste your text here"
          onChange={(e: any) => setText(e.target.value)}
          style={{ height: 280, fontSize: 13 }}
          value={text}
          maxLength={2048}
        />
        <div className="mt-2 mb-4 d-flex justify-content-end text-sm text-medium">
          {text?.length ?? 0} / 2048
        </div>
        {namedEntityRecognitionValue &&
          text &&
          !namedEntityRecognitionError && (
            <AppTagPicker
              options={optionLabels}
              cleanable
              className="mb-2"
              onChange={(newValue: string[]) => setLabels(newValue)}
              value={labels}
              onClean={() => setLabels([])}
              renderValue={(_: any, items: OptionLabelInterface[]) => {
                return items.map(
                  (item: OptionLabelInterface, index: number) => (
                    <Tag
                      key={index}
                      closable
                      style={{ backgroundColor: item.color }}
                      onClose={(e) => {
                        e.stopPropagation();
                        setLabels((prev) =>
                          prev.filter((value: string) => value !== item.value)
                        );
                      }}
                    >
                      {item.value}
                    </Tag>
                  )
                );
              }}
            />
          )}
        {namedEntityRecognitionLoading ? (
          <AppSpinner className="ml-auto" size="md" />
        ) : (
          <div className="mb-3 d-flex justify-content-end">
            {(namedEntityRecognitionValue || namedEntityRecognitionError) && (
              <Button
                onClick={() => handleRestart()}
                size="sm"
                className="m-0 p-0 mr-3"
                outline
                style={{ width: "65px", height: "32px", marginLeft: "10px" }}
              >
                Restart
              </Button>
            )}
            <Button
              onClick={() => handleClick()}
              color="primary"
              size="sm"
              className="m-0 p-0"
              style={{ width: "65px", height: "32px", marginLeft: "10px" }}
              disabled={showResult || !text}
            >
              Extract
            </Button>
          </div>
        )}
        {namedEntityRecognitionValue &&
          showResult &&
          highlightedString &&
          !namedEntityRecognitionError && (
            <>
              <div className="d-flex flex-column justify-content-start rounded border border-primary p-2">
                <p
                  className="text-sm"
                  dangerouslySetInnerHTML={{ __html: highlightedString }}
                />
              </div>
              {filteredData && labels.length > 0 && (
                <div className="d-flex justify-content-center mt-5">
                  <Table
                    withRowBorders
                    withColumnBorders
                    borderColor="#26B058"
                    withTableBorder
                  >
                    <Table.Thead>
                      <Table.Tr className="text-primary font-weight-">
                        <Table.Th className="border-primary text-center text-primary font-weight-normal">
                          Entity
                        </Table.Th>
                        <Table.Th className="border-primary text-center text-primary font-weight-normal">
                          Label
                        </Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {filteredData?.map(
                        (item: NamedEntityInterface, index: number) => {
                          return (
                            <Table.Tr key={index}>
                              <Table.Td className="border-primary text-center">
                                {item.entity}
                              </Table.Td>
                              <Table.Td className="font-weight-bold text-center border-primary text-uppercase text-small">
                                {item.label}
                              </Table.Td>
                            </Table.Tr>
                          );
                        }
                      )}
                    </Table.Tbody>
                  </Table>
                </div>
              )}
            </>
          )}
        {namedEntityRecognitionError && (
          <AppEmptyState
            hideIcon
            title="No Results"
            description="No results found for the provided text. Please restart and try again with different text."
          />
        )}
      </div>
    </>
  );
};

export default NamedEntityRecognition;
